import { Button, createListCollection, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { CustomSelect, Option } from '@components/ui/select/Select';
import { useChangeDroneClearanceMutation } from '@services/utm/droneClearance.api';
import { getClearanceStatusIcon } from '@utils/data/notification.utils';
import { addHours } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Target } from '@/types/c2/c2.types';
import { TargetLocation } from '@/types/sensor/sensor.types';
import { DataInfoSizeEnum } from '@/types/target.types';
import { DroneClearanceEnum } from '@/types/utm/drone.types';

type Props = {
  target: Target<TargetLocation>;
  isDisabled?: boolean;
};

const UNKNOWN = 'UNKNOWN';

enum ClearanceDurationEnum {
  ONE_HOUR = 'ONE_HOUR',
  INDEFINITELY = 'INDEFINITELY',
}

function getDateStringAtDuration(duration: ClearanceDurationEnum | null): string | null {
  switch (duration) {
    case ClearanceDurationEnum.ONE_HOUR:
      return addHours(new Date(), 1).toISOString();
    case ClearanceDurationEnum.INDEFINITELY:
    default:
      return null;
  }
}

function ClearanceSelect({ target, isDisabled = false }: Readonly<Props>) {
  const [changeDroneClearance, { isLoading }] = useChangeDroneClearanceMutation();
  const [selectedClearance, setSelectedClearance] = useState<DroneClearanceEnum | null | undefined>(target.clearance);
  const [selectedDuration, setSelectedDuration] = useState<ClearanceDurationEnum | undefined>();

  const formatMessage = useIntl().formatMessage;

  if (
    selectedClearance === null &&
    selectedDuration !== undefined &&
    selectedDuration !== ClearanceDurationEnum.INDEFINITELY
  ) {
    setSelectedDuration(ClearanceDurationEnum.INDEFINITELY);
  }

  const clearanceOptions = [
    {
      value: UNKNOWN,
      label: formatMessage({
        id: `clearance.UNKNOWN`,
      }),
    },
  ].concat(
    Object.keys(DroneClearanceEnum).map((key) => {
      return {
        value: key,
        label: formatMessage({ id: `clearance.${key}` }),
      };
    }),
  );

  const formatLabel = (option: Option) => (
    <HStack gap={1} width="100%">
      <Icon as={getClearanceStatusIcon(option.value as DroneClearanceEnum | null, DataInfoSizeEnum.TARGET_DETAIL)} />
      <Text textStyle="screenStd">{option.label}</Text>
    </HStack>
  );

  return (
    <VStack width="100%" gap="standardS">
      <CustomSelect
        value={selectedClearance === undefined ? (target.clearance ?? UNKNOWN) : (selectedClearance ?? UNKNOWN)}
        collection={createListCollection({ items: clearanceOptions })}
        disabled={isDisabled}
        size="sm"
        formatLabel={formatLabel}
        onChange={(selectedOption) =>
          setSelectedClearance(selectedOption === UNKNOWN ? null : (selectedOption as DroneClearanceEnum))
        }
      />
      <HStack width="100%" gap={0}>
        <Button
          key={ClearanceDurationEnum.ONE_HOUR}
          variant="toggle"
          size="toggleM"
          flex={1}
          disabled={selectedClearance === null || (selectedClearance === undefined && target.clearance === null)}
          data-active={selectedDuration === ClearanceDurationEnum.ONE_HOUR}
          onClick={() => setSelectedDuration(ClearanceDurationEnum.ONE_HOUR)}
        >
          <FormattedMessage id={`targetInfos.tab.drone.clearanceDuration.${ClearanceDurationEnum.ONE_HOUR}`} />
        </Button>
        <Button
          key={ClearanceDurationEnum.INDEFINITELY}
          variant="toggle"
          size="toggleM"
          flex={1}
          data-active={selectedDuration === ClearanceDurationEnum.INDEFINITELY}
          onClick={() => setSelectedDuration(ClearanceDurationEnum.INDEFINITELY)}
        >
          <FormattedMessage id={`targetInfos.tab.drone.clearanceDuration.${ClearanceDurationEnum.INDEFINITELY}`} />
        </Button>
      </HStack>
      <Button
        disabled={selectedClearance === undefined || selectedDuration === undefined}
        loading={isLoading}
        size="screenStd"
        onClick={() =>
          selectedClearance !== undefined &&
          selectedDuration !== undefined &&
          changeDroneClearance({
            id: target.displayId ?? target.uuid,
            site: target.site,
            clearance: selectedClearance,
            expiration: getDateStringAtDuration(selectedDuration),
            droneCode: target.droneCode,
          })
        }
      >
        <FormattedMessage id="global.validate" />
      </Button>
    </VStack>
  );
}

export default ClearanceSelect;
